import { useState, useEffect } from 'react';
import { Modal, Button, Accordion, ListGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faClock, faPaperPlane, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { sweetAlert } from "../../utils/UniversalFunction.js";
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";
import Loader from "../CompanyFolder/CompanyList/Loader.js";

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  hours = String(hours).padStart(2, '0');
  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
};

export default function ReminderButton({ itemId, status }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 576px)');

    const handleResize = () => setIsMobile(mediaQuery.matches);
    handleResize();

    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);


  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false); 

  useEffect(() => {
    if (isModalOpen) handleFetchReminderHistory(itemId);
  }, [isModalOpen, itemId]);

  const handleRecipientToggle = (recipient) => {
    setSelectedRecipients((prev) =>
      prev.includes(recipient)
        ? prev.filter((r) => r !== recipient)
        : [...prev, recipient]
    );
  };

  const handleSendReminder = async () => {
    if (selectedRecipients.length === 0) {
      sweetAlert("error", "Please select at least one recipient type.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        status,
        requestId: itemId,
        recipientType: selectedRecipients
      };

      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}reminder/send`,
        {},
        payload,
        'POST'
      );
      if (isSuccess) {
        sweetAlert("success", data?.message);
        handleCloseModal();
      } else {
        sweetAlert("error", data?.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      sweetAlert("error", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchReminderHistory = async (id) => {
    try {
      setLoading(true);
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}reminder/history`,
        {},
        { requestId: id },
        'GET'
      );
      if (isSuccess) {
        const formatted = data?.data.map(entry => ({
          ...entry,
          details: JSON.parse(entry.details)
        }));
        setReminderHistory(formatted);
      } else {
        sweetAlert("error", "Failed to fetch reminder history");
      }
    } catch (err) {
      console.error(err);
      sweetAlert("error", "Something went wrong while fetching history.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>

      {isMobile ? (
        <Button
          variant="light"
          className="p-1 rounded-circle"
          onClick={handleOpenModal}
          title="Send Reminder"
        >
          <FontAwesomeIcon icon={faBell} style={{ color: '#0050A4' }} />
        </Button>
      ) : (
        <p
          className="d-flex justify-content-center align-items-center mb-0 cursor-pointer"
          onClick={handleOpenModal}
        >
          Send Reminder
        </p>
      )}


      <Modal show={isModalOpen} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Send Reminder</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#0050A4' }} />
            </div>
          ) : (
            <>
              <Form>
                <Form.Label>Select recipient(s):</Form.Label>
                <div key="recipient-group" className="mb-3">
                  {['mainContact', 'siteAdmin', 'siteManager'].map((type) => (
                    <Form.Check
                      key={type}
                      inline
                      label={type === 'mainContact' ? 'Main Contact' : type === 'siteAdmin' ? 'Site Admin' : 'Site Manager'}
                      type="checkbox"
                      id={`recipient-${type}`}
                      name="recipient"
                      value={type}
                      checked={selectedRecipients.includes(type)}
                      onChange={() => handleRecipientToggle(type)}
                    />
                  ))}
                </div>
              </Form>

              <p className="text-muted">Review previous reminders before sending a new one</p>
              <div className="mt-4">
                <h6 className="text-muted mb-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faClock} className="me-2" /> Reminder History
                </h6>

                {reminderHistory.length > 0 ? (
                  <Accordion defaultActiveKey="0">
                    {reminderHistory.map((reminder, idx) => (
                      <Accordion.Item eventKey={idx.toString()} key={reminder.id}>
                        <Accordion.Header>
                          Sent by {reminder.senderName} on {formatDateTime(reminder.lastSentAt)}
                        </Accordion.Header>
                        <Accordion.Body>
                          {reminder.details.map((detail, i) => (
                            <ListGroup key={i} className="mb-3">
                              <ListGroup.Item><strong>Email:</strong> {detail.to}</ListGroup.Item>
                              <ListGroup.Item><strong>Name:</strong> {detail.context.fname}</ListGroup.Item>
                              <ListGroup.Item><strong>Lot Address:</strong> {detail.context.lotAddress}</ListGroup.Item>
                              <ListGroup.Item><strong>Due Date:</strong> {formatDateTime(detail.context.dueDate)}</ListGroup.Item>
                            </ListGroup>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                ) : (
                  <p className="text-muted">No previous reminders sent</p>
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={handleCloseModal}>
            <FontAwesomeIcon icon={faTimes} className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSendReminder} disabled={loading || selectedRecipients.length === 0}>
            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
            Send to {selectedRecipients.length > 0 ? selectedRecipients.map(r =>
              r === 'mainContact' ? 'Main Contact' :
                r === 'siteAdmin' ? 'Site Admin' :
                  'Site Manager'
            ).join(', ') : '...'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
